import { useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  trackAppointmentAbandonment,
  checkAndUploadAbandonmentLogs,
  trackProviderNotFound,
  trackPatientNotFound,
  markPageLoaded
} from '../utils/sentryTracking'

export const useJourneyTracking = ({
  trackingEnabled = false,
  userType,
  careType,
  language,
  currentStep,
  stepName,
  patientData = null,
  providerData = null,
  specialty = null,
  isRegistered = false,
  appType = null,
  selectedTime = ''
}) => {
  const sessionIdRef = useRef(null) 
  const isSubmittedRef = useRef(false)
  const hasTrackedUnloadRef = useRef(false) 
  const currentStepRef = useRef(currentStep)
  const stepNameRef = useRef(stepName)
  const patientDataRef = useRef(patientData)
  const providerDataRef = useRef(providerData)
  const specialtyRef = useRef(specialty)
  const isRegisteredRef = useRef(isRegistered)
  const trackingEnabledRef = useRef(trackingEnabled)
  const userTypeRef = useRef(userType)
  const careTypeRef = useRef(careType)
  const languageRef = useRef(language)
  const appTypeRef = useRef(appType)
  const selectedTimeRef = useRef(selectedTime)


  useEffect(() => {
    selectedTimeRef.current = selectedTime
  }, [selectedTime])

  useEffect(() => {
    selectedTimeRef.current = selectedTime
  }, [selectedTime])


  useEffect(() => {
    currentStepRef.current = currentStep
    stepNameRef.current = stepName
  }, [currentStep, stepName])

  useEffect(() => {
    patientDataRef.current = patientData
    providerDataRef.current = providerData
    specialtyRef.current = specialty
    isRegisteredRef.current = isRegistered
  }, [patientData, providerData, specialty, isRegistered])

  useEffect(() => {
    trackingEnabledRef.current = trackingEnabled
  }, [trackingEnabled])

  useEffect(() => {
    userTypeRef.current = userType
    careTypeRef.current = careType
    languageRef.current = language
    appTypeRef.current = appType
  }, [userType, careType, language, appType])

  useEffect(() => {
    checkAndUploadAbandonmentLogs()
    markPageLoaded()
  }, [])

  useEffect(() => {
    if (trackingEnabled && !sessionIdRef.current) {
      sessionIdRef.current = uuidv4()
    }
  }, [trackingEnabled])

  useEffect(() => {
    if (!trackingEnabled || !sessionIdRef.current) return

    const handleUnload = (event) => {
      if (hasTrackedUnloadRef.current) {
        return
      }

      // Track all steps including early ones (0, 0.5) if user has made progress
      // For early steps, only track if userType is set (user has made a selection)
      if ((currentStepRef.current === 0 || currentStepRef.current === 0.5) && !userTypeRef.current) {
        return
      }

      // For step 2 onwards without careType, still track the abandonment
      // The backend tracking logic will handle filtering if needed

      if (!isSubmittedRef.current && trackingEnabledRef.current && stepNameRef.current) {
        hasTrackedUnloadRef.current = true

        // Use refs to capture the exact current state at the moment of refresh
        trackAppointmentAbandonment({
          sessionId: sessionIdRef.current,
          reason: 'page_refresh_or_browser_close',
          currentStep: currentStepRef.current,
          stepName: stepNameRef.current,
          userType: userTypeRef.current,
          careType: careTypeRef.current,
          language: languageRef.current,
          patientData: patientDataRef.current,
          providerData: providerDataRef.current,
          specialty: specialtyRef.current,
          isRegistered: isRegisteredRef.current,
          appType: appTypeRef.current,
          selectedApptDate: selectedTimeRef.current?.start_time || null 
        })
      }
    }

    window.addEventListener('beforeunload', handleUnload)
    window.addEventListener('pagehide', handleUnload)

    return () => {
      window.removeEventListener('beforeunload', handleUnload)
      window.removeEventListener('pagehide', handleUnload)
    }
  }, [trackingEnabled])

  const markAsSubmitted = () => {
    isSubmittedRef.current = true
    hasTrackedUnloadRef.current = true 
  }

  const trackNoProviders = () => {
    if (!trackingEnabledRef.current) {
      return
    }

    if (!sessionIdRef.current) {
      return
    }

    trackProviderNotFound({
      sessionId: sessionIdRef.current,
      currentStep: currentStepRef.current,
      stepName: stepNameRef.current,
      userType: userTypeRef.current,
      careType: careTypeRef.current,
      language: languageRef.current,
      patientData: patientDataRef.current,
      specialty: specialtyRef.current,
      isRegistered: isRegisteredRef.current,
      appType: appTypeRef.current
    })
  }

  const trackPatientNotFoundAbandonment = (patientSearchData) => {
    if (!trackingEnabledRef.current) {
      return
    }

    if (!sessionIdRef.current) {
      sessionIdRef.current = uuidv4()
    }

    trackPatientNotFound({
      sessionId: sessionIdRef.current,
      userType: userTypeRef.current,
      language: languageRef.current,
      patientSearchData
    })
  }

  return {
    sessionId: sessionIdRef.current,
    markAsSubmitted,
    trackNoProviders,
    trackPatientNotFoundAbandonment
  }
}